import type { HttpApiError } from '@backmarket/http-api'

// an error that is specific to a field
type FieldErrorConfig<FormValues> = {
  // the kind of error (FIELD or TOAST)
  kind: 'FIELD'
  // the target of the error (can differ from the form field name, i.e. snake_case)
  target: string
  // the type of the error (i.e. /errors/invalid-email)
  type: string
  // the field to be updated with the error message
  formField: keyof FormValues
  // the error message to be shown in the field
  errorMessage: string
}

// an error that is to be shown as a toast
type ToastErrorConfig = {
  // the kind of error (FIELD or TOAST)
  kind: 'TOAST'
  // the target of the error (can differ from the form field name, i.e. snake_case)
  // there might be a target (sub-level error) or not (first-level error)
  target?: string
  // the type of the error (i.e. /errors/invalid-email)
  type: string
  // the error message to be shown in the toast
  errorMessage: {
    title?: string
    content?: string
  }
}

type ErrorConfig<FormValues> = FieldErrorConfig<FormValues> | ToastErrorConfig

/**
 * This function simplifies the management of validation errors returned by an API call.
 * It inspects both top-level and nested error objects and compares them against
 * a provided configuration list to determine whether each error should trigger
 * a field-specific error message or a toast notification.
 *
 */
export function handleValidationErrors<
  FormValues extends Record<string, unknown>,
>({
  error,
  errorsToHandleConfigs,
  setFieldErrors,
  setToastError,
}: {
  error: HttpApiError
  errorsToHandleConfigs: ErrorConfig<FormValues>[]
  setFieldErrors: (errors: Partial<Record<keyof FormValues, string>>) => void
  setToastError: (error?: { title?: string; content?: string }) => void
}) {
  const fieldErrors: Partial<Record<keyof FormValues, string>> = {}
  let hasFieldError = false
  let hasToastError = false
  let hasUnhandledError = false

  // handle first-level error
  const firstLevelErrorConfig = errorsToHandleConfigs.find(
    (config) => config.type === error.type,
  )
  if (firstLevelErrorConfig && firstLevelErrorConfig.kind === 'TOAST') {
    setToastError({
      title: firstLevelErrorConfig.errorMessage.title,
      content: firstLevelErrorConfig.errorMessage.content,
    })
    hasToastError = true
  } else {
    hasUnhandledError = true
  }

  // handle sub-errors
  error.errors.forEach((subError) => {
    const errorConfig = errorsToHandleConfigs.find(
      (config) =>
        config.target === subError.target && config.type === subError.type,
    )
    if (!errorConfig) {
      hasUnhandledError = true

      return
    }

    if (errorConfig.kind === 'FIELD') {
      fieldErrors[errorConfig.formField] = errorConfig.errorMessage
      hasFieldError = true

      return
    }

    if (errorConfig.kind === 'TOAST') {
      setToastError({
        title: errorConfig.errorMessage.title,
        content: errorConfig.errorMessage.content,
      })
      hasToastError = true
    }
  })

  if (Object.keys(fieldErrors).length > 0) {
    setFieldErrors(fieldErrors)
  }

  // If we have unhandled errors, and no specific field or toast errors, we show a generic error toast
  if (hasUnhandledError && !hasFieldError && !hasToastError) {
    setToastError()
  }
}
